import { useEffect, useMemo, useState } from "react";
import type { Config } from "vega-lite";
import { buildVegaConfig, type VizThemeOptions } from "./config.js";
import { detectDark, resolvePalette, type VizPalette } from "./tokens.js";

export interface VizTheme {
  /** The resolved color palette from the active theme. */
  palette: VizPalette;
  /** A ready-to-use Vega-Lite config built from {@link palette}. */
  config: Config;
  /** Whether the active theme is dark. */
  isDark: boolean;
}

export interface UseVizThemeOptions extends VizThemeOptions {
  /**
   * Element to read tokens from. Defaults to `document.documentElement`, which
   * is where `next-themes` toggles the `.dark` class.
   */
  root?: HTMLElement;
  /**
   * Changes when the theme's tokens change without a class/style mutation on
   * the observed elements (e.g. a scoped `<style>` tag is swapped); the
   * palette is read again whenever it does.
   */
  refreshKey?: string;
}

interface ThemeState {
  palette: VizPalette;
  isDark: boolean;
}

function readState(root?: HTMLElement): ThemeState {
  const isDark = detectDark(root);
  return { palette: resolvePalette(root, isDark), isDark };
}

/**
 * Resolves the chart palette from the DOM and keeps it in sync with theme
 * changes (the `.dark` class toggling) via a {@link MutationObserver}. Returns a
 * memoized Vega-Lite config so charts only re-embed when the theme actually
 * changes.
 */
export function useVizTheme(options?: UseVizThemeOptions): VizTheme {
  const { root, font, palette: paletteOverride, refreshKey } = options ?? {};
  const [state, setState] = useState<ThemeState>(() => readState(root));

  useEffect(() => {
    const target = root ?? document.documentElement;
    const update = () => setState(readState(root));
    update();

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    if (target !== document.documentElement) {
      observer.observe(target, {
        attributes: true,
        attributeFilter: ["class", "style"],
      });
    }
    return () => observer.disconnect();
  }, [root, refreshKey]);

  // Stable key so an inline `palette` override object doesn't force a rebuild
  // on every render.
  const overrideKey = paletteOverride ? JSON.stringify(paletteOverride) : "";
  const config = useMemo(
    () => buildVegaConfig(state.palette, { font, palette: paletteOverride }),
    [state.palette, font, overrideKey, paletteOverride],
  );

  return { palette: state.palette, config, isDark: state.isDark };
}
